import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon } from '@mantine/core';
import { useIntl } from 'react-intl';

import { shipmentDetailsSectionMessages } from './shipment-details-section-messages';
import styles from './ShipmentDetailsSection.module.css';
import { formatWallClockDate, formatWallClockTime, getTimeZoneAbbreviation } from '../../../../utils/datetime';
import { freightFormMessages } from '../../../freight-browser/components/freight-form/freight-form-messages';
import { DetailSection } from '../detail-section/DetailSection';

import type { LtlAddress, LtlAdditionalServices, LtlShipment } from '../../../../types/ltl';
import type { MessageDescriptor } from 'react-intl';

type ShipmentDetailsSectionProps = {
  shipment: LtlShipment;
  originAddress: LtlAddress | null;
  onCopy?: (value: string) => void;
};

type ServiceField = {
  key: keyof LtlAdditionalServices;
  label: MessageDescriptor;
};

// Mirrors the pickup accessorial key→message mapping in
// freight-browser/.../AccessorialsSection.tsx (lines 20-30); reuses those messages.
const PICKUP_SERVICE_FIELDS: ServiceField[] = [
  { key: 'isInsidePickup', label: freightFormMessages.accessorialInsidePickup },
  { key: 'isConstructionSitePickup', label: freightFormMessages.accessorialConstructionSitePickup },
  { key: 'isLiftgatePickup', label: freightFormMessages.accessorialLiftgatePickup },
  { key: 'isNotifyBeforePickup', label: freightFormMessages.accessorialNotifyBeforePickup },
  { key: 'isResidentialPickup', label: freightFormMessages.accessorialResidentialPickup },
  { key: 'isCarrierTerminalPickup', label: freightFormMessages.accessorialDropShipmentAtTerminal },
  { key: 'isGroceryConsolidationPickup', label: freightFormMessages.accessorialGroceryConsolidationPickup },
  { key: 'isLimitedAccessPickup', label: freightFormMessages.accessorialLimitedAccess },
  { key: 'isTradeShowPickup', label: freightFormMessages.accessorialTradeshowPickup },
];

// Mirrors the delivery accessorial key→message mapping (lines 32-43).
const DELIVERY_SERVICE_FIELDS: ServiceField[] = [
  { key: 'isInsideDelivery', label: freightFormMessages.accessorialInsideDelivery },
  { key: 'isConstructionSiteDelivery', label: freightFormMessages.accessorialConstructionSiteDelivery },
  { key: 'isLiftgateDelivery', label: freightFormMessages.accessorialLiftgateDelivery },
  { key: 'isNotifyBeforeDelivery', label: freightFormMessages.accessorialNotifyBeforeDelivery },
  { key: 'isResidentialDelivery', label: freightFormMessages.accessorialResidentialDelivery },
  { key: 'isHoldAtTerminal', label: freightFormMessages.accessorialHoldShipmentAtTerminal },
  { key: 'isGroceryConsolidationDelivery', label: freightFormMessages.accessorialGroceryConsolidationDelivery },
  { key: 'isAppointmentDelivery', label: freightFormMessages.accessorialAppointmentDelivery },
  { key: 'isLimitedAccessDelivery', label: freightFormMessages.accessorialLimitedAccess },
  { key: 'isTradeShowDelivery', label: freightFormMessages.accessorialTradeshowDelivery },
  { key: 'isSortAndSegregate', label: freightFormMessages.accessorialSortAndSegregate },
  { key: 'isProtectFromFreezing', label: freightFormMessages.accessorialProtectFromFreezing },
  { key: 'isProtectionFromHeat', label: freightFormMessages.accessorialProtectionFromHeat },
];

type AddressColumnProps = {
  title: string;
  address: LtlAddress | null;
  emptyLabel: string;
};

const AddressColumn = ({ title, address, emptyLabel }: AddressColumnProps) => {
  const cityStateLine = address ? [address.city, address.state, address.postalCode].filter(Boolean).join(', ') : '';

  return (
    <div className={styles.column}>
      <span className={styles.columnTitle}>{title}</span>
      {address === null ? (
        <span className={styles.empty}>{emptyLabel}</span>
      ) : (
        <>
          {address.name !== '' && <span className={styles.addressLine}>{address.name}</span>}
          {address.company !== '' && <span className={styles.addressLine}>{address.company}</span>}
          {address.line1 !== '' && <span className={styles.addressLine}>{address.line1}</span>}
          {address.line2 !== '' && <span className={styles.addressLine}>{address.line2}</span>}
          {cityStateLine !== '' && <span className={styles.addressLine}>{cityStateLine}</span>}
          {address.countryCode !== '' && <span className={styles.addressLine}>{address.countryCode}</span>}
          {address.phone !== '' && <span className={styles.addressLine}>{address.phone}</span>}
          {address.email !== '' && <span className={styles.addressLine}>{address.email}</span>}
        </>
      )}
    </div>
  );
};

type ServiceTagsProps = {
  title: string;
  fields: ServiceField[];
  services: LtlAdditionalServices;
  tradeshowName?: string | null;
  tradeshowBooth?: string | null;
};

const ServiceTags = ({ title, fields, services, tradeshowName, tradeshowBooth }: ServiceTagsProps) => {
  const intl = useIntl();
  const enabledFields = fields.filter((field) => services[field.key]);

  if (enabledFields.length === 0 && !tradeshowName) return null;

  return (
    <div className={styles.services}>
      <span className={styles.sectionTitle}>{title}</span>
      {enabledFields.length > 0 && (
        <div className={styles.tags}>
          {enabledFields.map((field) => (
            <span className={styles.tag} key={field.key}>
              {intl.formatMessage(field.label)}
            </span>
          ))}
        </div>
      )}
      {tradeshowName && (
        <span className={styles.tradeshowLine}>
          {intl.formatMessage(shipmentDetailsSectionMessages.tradeshowLine, {
            name: tradeshowName,
            booth: tradeshowBooth ?? '',
          })}
        </span>
      )}
    </div>
  );
};

/**
 * Left-column Shipment Details card: Ship From / Ship To addresses plus
 * pickup/delivery accessorial service tags, pickup window, and instructions.
 * Store-agnostic — all data arrives via props.
 */
export const ShipmentDetailsSection = ({ shipment, originAddress, onCopy }: ShipmentDetailsSectionProps) => {
  const intl = useIntl();
  const {
    additionalServices,
    pickupReadyTime,
    pickupCloseTime,
    pickupDate,
    pickupNumber,
    pickupInstructions,
    deliveryInstructions,
    handlingInstructions,
  } = shipment;

  const ianaTimeZone = originAddress?.ianaTimeZone;
  const readyFormatted = formatWallClockTime(pickupReadyTime) || null;
  const closeFormatted = formatWallClockTime(pickupCloseTime) || null;
  const dateFormatted = formatWallClockDate(pickupDate) || null;
  const hasPickupWindow = readyFormatted !== null && closeFormatted !== null;
  const tzAbbreviation = getTimeZoneAbbreviation(ianaTimeZone, pickupDate);

  return (
    <DetailSection testId='shipment-details-section' title={intl.formatMessage(shipmentDetailsSectionMessages.sectionTitle)}>
      <div className={styles.columns}>
        <AddressColumn
          address={originAddress}
          emptyLabel={intl.formatMessage(shipmentDetailsSectionMessages.emptyAddress)}
          title={intl.formatMessage(shipmentDetailsSectionMessages.shipFromTitle)}
        />
        <AddressColumn
          address={shipment.destinationAddress}
          emptyLabel={intl.formatMessage(shipmentDetailsSectionMessages.emptyAddress)}
          title={intl.formatMessage(shipmentDetailsSectionMessages.shipToTitle)}
        />
      </div>

      {additionalServices !== null && (
        <ServiceTags
          fields={PICKUP_SERVICE_FIELDS}
          services={additionalServices}
          title={intl.formatMessage(shipmentDetailsSectionMessages.pickupServicesTitle)}
          tradeshowBooth={additionalServices.tradeshowPickupBoothNumber}
          tradeshowName={additionalServices.tradeshowPickupName}
        />
      )}

      {hasPickupWindow && (
        <div className={styles.section}>
          <span className={styles.sectionTitle}>{intl.formatMessage(shipmentDetailsSectionMessages.pickupWindowTitle)}</span>
          <span className={styles.sectionValue}>
            {dateFormatted
              ? intl.formatMessage(shipmentDetailsSectionMessages.pickupWindowRange, {
                  start: readyFormatted,
                  end: closeFormatted,
                  date: dateFormatted,
                  tz: tzAbbreviation ? ` ${tzAbbreviation}` : '',
                })
              : `${readyFormatted} - ${closeFormatted}${tzAbbreviation ? ` ${tzAbbreviation}` : ''}`}
          </span>
          {pickupNumber && (
            <div className={styles.pickupNumberRow}>
              <span className={styles.sectionValue}>
                {intl.formatMessage(shipmentDetailsSectionMessages.pickupNumberLabel, { number: pickupNumber })}
              </span>
              {onCopy && (
                <ActionIcon aria-label='Copy pickup number' onClick={() => onCopy(pickupNumber)} size='sm' variant='subtle'>
                  <FontAwesomeIcon icon={faCopy} />
                </ActionIcon>
              )}
            </div>
          )}
        </div>
      )}

      {pickupInstructions && (
        <div className={styles.section}>
          <span className={styles.sectionTitle}>{intl.formatMessage(shipmentDetailsSectionMessages.pickupInstructionsTitle)}</span>
          <span className={styles.instructions}>{pickupInstructions}</span>
        </div>
      )}

      {additionalServices !== null && (
        <ServiceTags
          fields={DELIVERY_SERVICE_FIELDS}
          services={additionalServices}
          title={intl.formatMessage(shipmentDetailsSectionMessages.deliveryServicesTitle)}
          tradeshowBooth={additionalServices.tradeshowDeliveryBoothNumber}
          tradeshowName={additionalServices.tradeshowDeliveryName}
        />
      )}

      {deliveryInstructions && (
        <div className={styles.section}>
          <span className={styles.sectionTitle}>{intl.formatMessage(shipmentDetailsSectionMessages.deliveryInstructionsTitle)}</span>
          <span className={styles.instructions}>{deliveryInstructions}</span>
        </div>
      )}

      {handlingInstructions && (
        <div className={styles.section}>
          <span className={styles.sectionTitle}>{intl.formatMessage(shipmentDetailsSectionMessages.handlingInstructionsTitle)}</span>
          <span className={styles.instructions}>{handlingInstructions}</span>
        </div>
      )}
    </DetailSection>
  );
};
